import type { ApplicationStatus } from '@/types/applications';
import type { ChipTone } from '@/components/editorial/StatusChip';
import type { DOCUMENT_KIND_LABELS } from '@/lib/documents';

// Epic #1037: every state the backend can return, INCLUDING the two legacy
// strings — a row the migration has not reached yet must still render a
// label, never crash a queue over a chip.
export const APPLICATION_STATUS_DISPLAY: Record<ApplicationStatus, { label: string; tone: ChipTone }> = {
  draft:              { label: 'Draft',               tone: 'neutral' },
  submitted:          { label: 'Submitted',           tone: 'sky'     },
  under_review:       { label: 'Under review',        tone: 'warn'    },
  needs_more_info:    { label: 'Needs more info',     tone: 'warn'    },
  // Application review redesign (2026-10-10): the end of the staff review,
  // not a recommendation. Approve / waitlist / decline is recommended once, on
  // the Committee package page. Staff word only: a family reads "Under review".
  staff_recommended:  { label: 'Ready for committee', tone: 'sky'     },
  committee_approved: { label: 'Committee approved',  tone: 'ok'      },
  staff_declined:     { label: 'Declined',            tone: 'danger'  },
  waitlisted:         { label: 'Waitlisted',          tone: 'violet'  },
  // Until DocuSign (#1043) there is no letter: Send is a welcome email and the
  // family's acceptance is recorded by hand (DECISIONS 2026-10-12, "Intake E2E
  // wave rulings" / #2735, #2761). So offer_sent and offer_signed name those
  // two events, as the offer panel does ("Welcome email sent …", "Waiting for
  // the family to accept"). Victor's "Admission letter" staff word
  // (2026-09-13) stays on the DocuSign-only endings below, which no code path
  // reaches today. Status KEYS keep `offer_*`; only labels changed. `(?)`
  // admission-letter-vocabulary in lib/pending-decisions.ts.
  offer_sent:         { label: 'Welcome email sent',        tone: 'teal'    },
  offer_signed:       { label: 'Acceptance recorded',       tone: 'teal'    },
  // AP-51 (#1072). Separate chips, not one "not signed", because each needs a
  // different next action from a TRFer — the whole reason they are separate states.
  offer_declined:     { label: 'Admission letter declined', tone: 'danger'  },
  offer_voided:       { label: 'Admission letter voided',   tone: 'warn'    },
  admitted:           { label: 'Admitted',            tone: 'ok'      },
  withdrawn:          { label: 'Withdrawn',           tone: 'neutral' },
  // Legacy (pre-#1037) — readable until `migrate-application-statuses.mjs` runs.
  approved:           { label: 'Approved (legacy)',   tone: 'ok'      },
  denied:             { label: 'Not approved (legacy)', tone: 'danger' },
  // Legacy (#2235) — merged into offer_voided; readable until
  // `migrate-offer-expired-to-voided.mjs` runs.
  offer_expired:      { label: 'Admission letter expired (legacy)', tone: 'warn' },
};

/**
 * WHAT A FAMILY IS ALLOWED TO SEE (#1271, rule from #998).
 *
 * The map above is the STAFF vocabulary: fourteen states, several of which are
 * steps inside TRF's own process rather than anything a family has been told.
 * The family status screen cannot narrate all fourteen, and it must not
 * enumerate them by hand either — that is exactly how #1271 happened. The
 * screen listed `approved` / `denied`, epic #1037 replaced those with a chain
 * (`staff_recommended → committee_approved → offer_sent → offer_signed →
 * admitted`), `migrate-application-statuses.mjs` rewrote the rows, and from
 * that moment every decided application rendered an unlit decision step and a
 * paragraph promising a decision that had already been made.
 *
 * So the screen reads a STAGE, not a status, and this is the one place the
 * fourteen collapse into it. Adding a fifteenth status makes the compiler ask
 * for its stage here rather than leaving a screen silently dark.
 *
 * The rule that decides the collapse is #998: nothing about a decision is
 * family-visible before `offer_sent`. Hence the asymmetry below, which is
 * deliberate and is the whole content of this function:
 * - `staff_recommended` and `committee_approved` are STILL `review`. The
 *   decision exists; it is not the family's to know until the offer goes out.
 * - `staff_declined` and `waitlisted` are revealed when they are recorded.
 *   TRF writes family-audience copy for both outcomes
 *   (`apply.status.body_denied`, `apply.status.body_waitlisted`), and
 *   withholding them would leave a declined family reading "we'll be in touch
 *   when a decision is made" — the #1135 defect, re-made in new statuses.
 */
export type FamilyVisibleStage =
  | 'none'
  | 'submitted'
  | 'review'
  | 'approved'
  | 'declined'
  | 'waitlisted';

const FAMILY_VISIBLE_STAGE: Record<ApplicationStatus, FamilyVisibleStage> = {
  // Nothing to show. `draft` never reaches the status screen (the page sends it
  // back to the wizard) and `withdrawn` ended the application without an
  // outcome — the 2026-08-23 ruling named three outcomes and this is not one.
  draft:              'none',
  withdrawn:          'none',
  submitted:          'submitted',
  under_review:       'review',
  needs_more_info:    'review',
  // #998 — recorded, not revealed.
  staff_recommended:  'review',
  committee_approved: 'review',
  staff_declined:     'declined',
  waitlisted:         'waitlisted',
  // From here the family is holding the offer, so the approval is theirs.
  offer_sent:         'approved',
  offer_signed:       'approved',
  admitted:           'approved',
  // AP-51 (#1072). All three stay `approved`, and that is deliberate: this map
  // narrates TRF's DECISION, not the envelope's fate, and an offer that lapsed
  // or was voided does not un-approve a family. `declined` would be actively
  // wrong — it reads as TRF declining them — and `offer_declined` is the
  // family's OWN answer, so telling them they were declined would be the
  // platform reporting their decision back to them as ours.
  //
  // What changes is on the staff side: each carries its own next action
  // (`nextActionForStatus`, services/docusign/envelopeOutcome.js), shown to
  // staff through `offerNextAction` below (#2159). Worth a copy review if the
  // family status screen ever narrates the offer itself.
  offer_declined:     'approved',
  offer_voided:       'approved',
  // Legacy rows: the stage the migrations will give them.
  approved:           'approved',
  denied:             'declined',
  offer_expired:      'approved', // #2235 → offer_voided
};

/**
 * #2159 — what a TRFer does next with an admission letter that ended unsigned.
 *
 * A COPY of backend `nextActionForStatus` (services/docusign/envelopeOutcome.js,
 * the `declined` and `voided` rows of ENVELOPE_OUTCOMES). The application API
 * does not carry the sentence, so the staff review page and the queue row read
 * it from here; `applications-display.next-action.test.ts` reads the backend
 * file and fails if either sentence drifts. Staff UI, English only.
 *
 * Two states, not three: #2235 merged `offer_expired` into `offer_voided`, and
 * the backend gives the voided row the void's sentence (the `expired` event's
 * own sentence is never what `nextActionForStatus` returns). A legacy
 * `offer_expired` row gets nothing, as it does from the backend; its migration
 * makes it a voided row. `offer_signed` is left out on purpose: until DocuSign
 * (#1043) it means "acceptance recorded", and its next step already has its
 * own panel on the review page.
 */
export const OFFER_NEXT_ACTION: Readonly<Partial<Record<ApplicationStatus, string>>> = {
  offer_declined: 'Talk to the family before re-sending. A re-send on its own is the same offer again.',
  offer_voided: 'The envelope was cancelled. Fix the document, then re-send the offer.',
};

export const offerNextAction = (status: ApplicationStatus): string | null =>
  OFFER_NEXT_ACTION[status] ?? null;

/** The label both surfaces put before the sentence, kept with it. */
export const OFFER_NEXT_ACTION_LABEL = 'Next step';

/**
 * A status not in the map is a state this build has never heard of — a newer
 * backend against an older bundle. A family gets a plain, undecided screen
 * rather than a crash or a decision nobody made.
 */
export const familyVisibleStage = (status: ApplicationStatus): FamilyVisibleStage =>
  FAMILY_VISIBLE_STAGE[status] ?? 'none';

/**
 * The status a family may be shown BY NAME, for the chip.
 *
 * Two of the fourteen labels name a decision #998 keeps back, and a chip
 * reading "Committee approved" in decision-green leaks it as loudly as any
 * email — and contradicts the timeline underneath it, which says the
 * application is still under review. They borrow `under_review`'s label and
 * tone. Every other status keeps its own: `needs_more_info` in particular must
 * stay itself, because the panel below it is an instruction to the family.
 */
const NOT_FAMILY_NAMEABLE = new Set<ApplicationStatus>(['staff_recommended', 'committee_approved']);

/**
 * #2235: `offer_expired` was merged into `offer_voided`. A row the migration
 * has not reached yet is named as the voided letter it will become, so the
 * family catalog carries no "expired" word for a state that no longer exists.
 */
const FAMILY_ALIAS: Partial<Record<ApplicationStatus, ApplicationStatus>> = { offer_expired: 'offer_voided' };

export const familyVisibleStatus = (status: ApplicationStatus): ApplicationStatus =>
  NOT_FAMILY_NAMEABLE.has(status) ? 'under_review' : (FAMILY_ALIAS[status] ?? status);

/**
 * #2236 — the statuses a family can withdraw from: every stage before
 * admission. Mirrors backend `WITHDRAWABLE_APPLICATION_STATUSES`
 * (FamilyApplication.js), which is derived from LEGAL_TRANSITIONS and pinned
 * in tests/models/application-status-machine.test.js. Not `offer_signed` —
 * the signature is in and admission is the system's next step — and never a
 * legacy string, which no transition leaves.
 */
export const WITHDRAWABLE_STATUSES: ReadonlySet<ApplicationStatus> = new Set<ApplicationStatus>([
  'draft', 'submitted', 'under_review', 'needs_more_info', 'staff_recommended',
  'committee_approved', 'waitlisted', 'offer_sent', 'offer_declined', 'offer_voided',
]);

export const canWithdraw = (status: ApplicationStatus): boolean => WITHDRAWABLE_STATUSES.has(status);

/**
 * #2927 — the statuses staff may DELETE an application from: every
 * withdrawable one plus the three closed-but-not-admitted ones. Never
 * `admitted` (it owns a family and grants), never `offer_signed` (admission is
 * next), never a legacy string. Mirrors backend DELETABLE_APPLICATION_STATUSES
 * (services/applicationDeletion.js), pinned against the model in
 * tests/models/application-deletion-statuses.test.js.
 */
export const DELETABLE_STATUSES: ReadonlySet<ApplicationStatus> = new Set<ApplicationStatus>([
  ...WITHDRAWABLE_STATUSES, 'withdrawn', 'staff_declined',
]);

export const canDelete = (status: ApplicationStatus): boolean => DELETABLE_STATUSES.has(status);

/**
 * Mario, 2026-10-13 — the statuses the FAMILY may withdraw from on its status
 * page: only before committee approval. From `committee_approved` on, one
 * mis-tap would cost an approved family its place, so the family tells TRF and
 * staff record it (`canWithdraw` above stays the staff rule). Mirrors backend
 * FAMILY_WITHDRAWABLE_APPLICATION_STATUSES (FamilyApplication.js); the family
 * route refuses the rest with WITHDRAW_NEEDS_STAFF.
 */
export const FAMILY_WITHDRAWABLE_STATUSES: ReadonlySet<ApplicationStatus> = new Set<ApplicationStatus>([
  'draft', 'submitted', 'under_review', 'needs_more_info', 'staff_recommended', 'waitlisted',
]);

export const canFamilyWithdraw = (status: ApplicationStatus): boolean => FAMILY_WITHDRAWABLE_STATUSES.has(status);

/**
 * The family-visible status names as KEYS (#2109 R3-B4), for the family status
 * screen. The map above stays the staff vocabulary in English until R4; the
 * parity test pins each en catalog value to its `APPLICATION_STATUS_DISPLAY`
 * label, so the two cannot drift. The two statuses `familyVisibleStatus`
 * never names are absent on purpose — a key for them would be a translation
 * of a word no family is shown.
 */
export type FamilyNameableStatus = Exclude<ApplicationStatus, 'staff_recommended' | 'committee_approved' | 'offer_expired'>;

export const FAMILY_STATUS_LABEL_KEY = {
  draft: 'applications:status.draft',
  submitted: 'applications:status.submitted',
  under_review: 'applications:status.under_review',
  needs_more_info: 'applications:status.needs_more_info',
  staff_declined: 'applications:status.staff_declined',
  waitlisted: 'applications:status.waitlisted',
  offer_sent: 'applications:status.offer_sent',
  offer_signed: 'applications:status.offer_signed',
  offer_declined: 'applications:status.offer_declined',
  offer_voided: 'applications:status.offer_voided',
  admitted: 'applications:status.admitted',
  withdrawn: 'applications:status.withdrawn',
  approved: 'applications:status.approved',
  denied: 'applications:status.denied',
} as const satisfies Record<FamilyNameableStatus, string>;

/** The key for the chip a family reads — `familyVisibleStatus` first, so a withheld decision never gets a name. */
export function familyStatusLabelKey(status: ApplicationStatus): (typeof FAMILY_STATUS_LABEL_KEY)[FamilyNameableStatus] | null {
  const shown = familyVisibleStatus(status) as FamilyNameableStatus;
  return FAMILY_STATUS_LABEL_KEY[shown] ?? null;
}

/** A requested document's kind as a family reads it (#2109 R3-B22: no raw enum on screen). */
export const DOCUMENT_KIND_LABEL_KEY = {
  diagnosis_letter: 'applications:documentKind.diagnosis_letter',
  income_verification: 'applications:documentKind.income_verification',
  bill_statement: 'applications:documentKind.bill_statement',
  child_photo: 'applications:documentKind.child_photo',
  family_photo: 'applications:documentKind.family_photo',
} as const satisfies Record<keyof typeof DOCUMENT_KIND_LABELS, string>;

/**
 * THE DESK LANES (2026-09-20) — the grouping axis for the Applications queue.
 *
 * Guideline §3.2 forbids grouping a list by status, and it is right to: a
 * header row is a sentence the reader cannot skip, status changes every time
 * anyone touches the record, and there are seventeen of them above. Grouping
 * by the raw key would print a dozen one-row headers and rebuild the pipeline
 * board `/trfer/pipeline` was retired into this page to replace.
 *
 * So the queue groups by LANE: four buckets that pass §3.2's test — few,
 * stable, and each changing what the TRFer does with the row. The lane answers
 * "whose move is it?", which is the question a queue exists to answer:
 *   To read           — nobody has read it yet, or it is being read.
 *   Waiting on family — TRF asked for something and is waiting.
 *   Deciding          — TRF's decision chain, from recommendation to signature,
 *                       and waitlists TRF will look at again.
 *   Closed            — the application reached an end.
 *
 * The lane is NOT the status, so the Status column stays while grouped (§1.1
 * hides a grouped attribute's column; "Deciding" does not tell a reader
 * whether the letter went out). The four names are engineering's proposal —
 * `(?)` `application-status-lanes` in lib/pending-decisions.ts.
 *
 * Typed `Record<ApplicationStatus, …>` on purpose, the same device
 * `FAMILY_VISIBLE_STAGE` uses above: an eighteenth status makes the compiler
 * ask which lane it belongs in rather than letting it drift to a fallback.
 */
export type ApplicationLane = 'to_read' | 'waiting' | 'deciding' | 'closed';

/** Lane order on the page — the order a TRFer works them. */
export const APPLICATION_LANES: readonly ApplicationLane[] = ['to_read', 'waiting', 'deciding', 'closed'];

export const APPLICATION_LANE_LABEL: Record<ApplicationLane, string> = {
  to_read: 'To read',
  waiting: 'Waiting on family',
  deciding: 'Deciding',
  closed: 'Closed',
};

const APPLICATION_LANE: Record<ApplicationStatus, ApplicationLane> = {
  // Unread work. `draft` sits here rather than in Closed because a started
  // application is a family mid-ask, not an ended one — it is the row a
  // referral chase is about.
  draft:              'to_read',
  submitted:          'to_read',
  under_review:       'to_read',
  // The one status where the ball is the family's.
  needs_more_info:    'waiting',
  // TRF's decision chain.
  staff_recommended:  'deciding',
  committee_approved: 'deciding',
  offer_sent:         'deciding',
  offer_signed:       'deciding',
  // AP-51 (#1072): a voided letter is TRF's move to make again — it fell OUT
  // of the chain and has to re-enter it, so it stays beside it.
  // `offer_declined` is the family's own answer and ends the application.
  offer_voided:       'deciding',
  // #2235: legacy, merged into offer_voided — it sits where it will land.
  offer_expired:      'deciding',
  // #2533: a waitlist is still open — TRF looks at it again on its
  // review-again date or when the next cycle opens. Decided, not final, so it
  // sits with the rest of TRF's decision work rather than under Closed.
  waitlisted:         'deciding',
  offer_declined:     'closed',
  // Ended, whatever the outcome.
  admitted:           'closed',
  staff_declined:     'closed',
  withdrawn:          'closed',
  // Legacy (pre-#1037) rows the migration has not reached.
  approved:           'closed',
  denied:             'closed',
};

/**
 * A status this build has never heard of lands in To read — the lane a TRFer
 * actually opens. The alternative, dropping it or hiding it under Closed, is
 * how an application goes unread because a backend shipped first.
 */
export const applicationLane = (status: ApplicationStatus): ApplicationLane =>
  APPLICATION_LANE[status] ?? 'to_read';
